import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Download, Loader2, Check } from 'lucide-react';

/* ─── Admin → Payouts tab (due, history, CSV exports) ─── */
export const AdminPayoutsTab = ({
  payouts, payoutHistory, loadingHistory, markingPaidId, handleMarkPaid,
  exportPayoutsCSV, exportPayoutHistoryCSV,
}) => {
  return (
          <div className="lm-enter">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-2xl font-bold" data-testid="payouts-heading">Affiliate Payouts</h2>
              {payouts?.rows?.length > 0 && (
                <Button variant="outline" size="sm" onClick={exportPayoutsCSV} data-testid="export-payouts-btn">
                  <Download className="w-4 h-4 mr-1.5" /> Export CSV
                </Button>
              )}
            </div>

            {!payouts ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <Card className="p-5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Total pending</p>
                    <p className="text-2xl font-semibold tracking-tight" data-testid="total-pending">€{payouts.total_due.toFixed(2)}</p>
                  </Card>
                  <Card className="p-5 border-emerald-500/30 bg-emerald-500/[0.03]">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Ready to pay</p>
                    <p className="text-2xl font-semibold tracking-tight text-emerald-600" data-testid="ready-total">€{payouts.ready_total?.toFixed(2) || '0.00'}</p>
                  </Card>
                  <Card className="p-5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Affiliates</p>
                    <p className="text-2xl font-semibold tracking-tight">{payouts.rows.length}</p>
                  </Card>
                  <Card className="p-5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Min threshold</p>
                    <p className="text-2xl font-semibold tracking-tight">€{payouts.min_threshold.toFixed(2)}</p>
                  </Card>
                </div>

                {payouts.rows.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">No pending payouts.</div>
                ) : (
                  <div className="space-y-3">
                    {payouts.rows.map((p) => (
                      <Card key={p.user_id} className={`p-4 ${p.ready ? 'border-emerald-500/40' : 'border-amber-500/40'}`} data-testid={`payout-row-${p.user_id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Link to={`/${p.username}`} target="_blank" className="font-semibold hover:underline">@{p.username}</Link>
                              <span className="text-sm text-muted-foreground">— {p.name}</span>
                              {!p.payout_info?.method && <Badge variant="outline" className="text-xs">No payout method</Badge>}
                              {p.payout_info?.method === 'paypal' && <Badge variant="outline" className="text-xs">PayPal</Badge>}
                              {p.payout_info?.method === 'bank' && <Badge variant="outline" className="text-xs">Bank (SEPA)</Badge>}
                              {p.pending_total < payouts.min_threshold && <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/40">Below threshold</Badge>}
                              {p.self_referrals > 0 && <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/40" title="Self-referrals are net-positive for us (they paid 4× what they get back) but worth a quick look">FYI: {p.self_referrals} self-referral</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {p.monthly_commissions} monthly × €1 + {p.yearly_commissions} yearly × €10 ·
                              {' '}{p.unique_buyers} unique buyer(s) · {p.unique_domains} domain(s)
                            </p>
                            {p.payout_info?.method === 'paypal' && (
                              <p className="text-xs font-mono text-muted-foreground mt-1">→ {p.payout_info.paypal_email}</p>
                            )}
                            {p.payout_info?.method === 'bank' && (
                              <p className="text-xs font-mono text-muted-foreground mt-1">→ {p.payout_info.account_holder} · {p.payout_info.iban}{p.payout_info.bic ? ` · ${p.payout_info.bic}` : ''}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Pending</p>
                              <p className="text-xl font-semibold tabular-nums text-emerald-600">€{p.pending_total.toFixed(2)}</p>
                            </div>
                            <Button
                              size="sm"
                              disabled={markingPaidId === p.user_id || !p.ready}
                              onClick={() => handleMarkPaid(p.user_id, p.pending_total)}
                              data-testid={`mark-paid-${p.user_id}`}>
                              {markingPaidId === p.user_id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <><Check className="w-3.5 h-3.5 mr-1.5" />Mark paid</>}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                <Card className="p-4 mt-6 bg-muted/30">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <strong>Why no fraud blocking?</strong> Every commission is paid from a real Stripe payment — you keep 75% (€3 monthly / €30 yearly), affiliate gets 25%. Self-referrals are net-positive for you (affiliate paid 4× what they get back). The buyer/domain stats are shown for awareness only.
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                    <strong>Workflow:</strong> Export CSV → process payouts in PayPal Mass Pay / SEPA → click <strong>Mark paid</strong>. Payouts unlock at <strong>€{payouts.min_threshold}</strong> with a payout method on file. Stripe handles chargeback fraud on the buyer side.
                  </p>
                </Card>

                {/* Payout History — every Mark-paid click logged with note */}
                <div className="mt-10">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold" data-testid="payout-history-heading">Payout History</h3>
                      {payoutHistory && (
                        <p className="text-xs text-muted-foreground">
                          {payoutHistory.total} payout(s) · <span className="font-semibold text-foreground">€{payoutHistory.grand_total_paid?.toFixed(2) || '0.00'}</span> paid out lifetime
                        </p>
                      )}
                    </div>
                    {payoutHistory?.rows?.length > 0 && (
                      <Button variant="outline" size="sm" onClick={exportPayoutHistoryCSV} data-testid="export-payout-history-btn">
                        <Download className="w-4 h-4 mr-1.5" /> Export CSV
                      </Button>
                    )}
                  </div>
                  {loadingHistory ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                  ) : !payoutHistory?.rows?.length ? (
                    <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="payout-history-empty">
                      No payouts marked yet. They&apos;ll appear here after you click &quot;Mark paid&quot;.
                    </Card>
                  ) : (
                    <div className="space-y-2" data-testid="payout-history-list">
                      {payoutHistory.rows.map((p) => (
                        <Card key={p.id} className="p-3" data-testid={`payout-history-row-${p.id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Link to={`/${p.username}`} target="_blank" className="font-semibold text-sm hover:underline">@{p.username}</Link>
                                <span className="text-xs text-muted-foreground">— {p.name || p.email}</span>
                                <Badge variant="outline" className="text-[10px]">{p.commission_count} commission(s)</Badge>
                              </div>
                              {p.paypal_email && (
                                <p className="text-xs font-mono text-muted-foreground mt-1">→ {p.paypal_email}</p>
                              )}
                              {p.note ? (
                                <p className="text-xs mt-1.5 rounded-md bg-muted/40 px-2 py-1 inline-block">
                                  <span className="text-muted-foreground">Note:</span> {p.note}
                                </p>
                              ) : (
                                <p className="text-xs text-muted-foreground/60 italic mt-1.5">No note left</p>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-lg font-semibold tabular-nums text-emerald-600">€{p.amount.toFixed(2)}</p>
                              <p className="text-[11px] text-muted-foreground">{p.paid_at ? new Date(p.paid_at).toLocaleString() : ''}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
  );
};
